// Browser-side agent that remote-controls the logged-in player. A `/agent <goal>` chat
// command starts it; `/agent stop` returns control to the human. While active it:
//   1. fully takes over input (disables the human keyboard/mouse handlers),
//   2. every ~DECISION_INTERVAL_MS: builds an observation from the client's own view,
//      POSTs it to the server's /agent/decide proxy (which calls Claude), and applies the
//      returned action via the existing Client input methods,
//   3. re-applies its held movement intent every frame (onFrame), because reconciliation
//      overwrites the local player's speed2 each frame.
// The human watches everything in their own browser — the agent's inputs produce the same
// server updates and rendering a human's would.
import { Client } from "../client"
import { applyAction } from "./actions"
import { buildObservation } from "./observation"
import { AgentAction, DecideRequest, DecideResponse } from "./types"

// Tick cadence floor (ms). Overridable via AGENT_INTERVAL_MS to trade cost vs. responsiveness.
const DECISION_INTERVAL_MS =
    Number((typeof process !== "undefined" && process.env && process.env.AGENT_INTERVAL_MS) || "") || 1100
const HISTORY_LIMIT = 8
const CHAT_LIMIT = 16

// Named goal presets so common objectives don't have to be typed out in full.
// `/agent <preset>` (e.g. `/agent survive`) expands to the mapped goal; any other text is
// used verbatim as a free-form goal.
const PRESETS: Record<string, string> = {
    survive:
        "Survive and sustain the camp indefinitely: keep the campfire fed with wood (chop nearby " +
        "trees yourself and/or send foragers to forage and feed the fire), defend the camp and " +
        "yourself from hostiles (attack them, or command defenders to engage), eat food when your " +
        "hunger gets low, pick up any wood or food nearby, and retreat if your hp gets low.",
    defend:
        "Protect the camp: watch for hostiles and deal with them fast — attack the nearest threat " +
        "yourself and command the defenders to engage, hold the line near the Lord and campfire, " +
        "and retreat to heal only if your hp gets critically low.",
    gather:
        "Keep the camp supplied: chop nearby trees for wood and/or command foragers to forage wood " +
        "and feed the campfire so it never dies down, and pick up any wood or food lying around.",
}

function decideEndpoint(): string {
    // Prod: derive from the WS API host (wss://host -> https://host). Local dev (Parcel on
    // :1234, server on :8080): talk to the server's HTTP port on the same hostname.
    const apiHost = (typeof process !== "undefined" && process.env && process.env.API_HOST) || ""
    if (apiHost) return apiHost.replace(/^ws/, "http").replace(/\/?$/, "") + "/agent/decide"
    const loc = typeof location !== "undefined" ? location : ({ protocol: "http:", hostname: "localhost" } as any)
    return `${loc.protocol}//${loc.hostname}:8080/agent/decide`
}

export class AgentController {
    private active = false
    private goal = ""
    private tick = 0
    private heldSpeed2: { x: number; y: number } | null = null
    private history: Array<{ tick: number; action: AgentAction }> = []
    private recentChat: Array<{ from: string; message: string }> = []
    private loopTimer: any = null
    private inFlight = false

    constructor(private client: Client) {}

    isActive() {
        return this.active
    }

    /** Handle a `/agent ...` chat command. Returns true if it was consumed. */
    handleCommand(raw: string): boolean {
        const rest = raw.replace(/^\/agent\b/, "").trim()
        if (rest === "" || rest === "help") {
            const presets = Object.keys(PRESETS).join(", ")
            this.say(
                `Agent: \`/agent <goal>\` to play a free-form goal, \`/agent stop\` to take back over. ` +
                    `Presets: ${presets} (e.g. \`/agent survive\`).`,
            )
            return true
        }
        if (rest === "stop" || rest === "off") {
            this.stop("You have control again.")
            return true
        }
        // Expand a known preset keyword; otherwise use the text as a free-form goal.
        const preset = PRESETS[rest.toLowerCase()]
        this.start(preset || rest)
        return true
    }

    start(goal: string) {
        this.goal = goal
        this.tick = 0
        this.history = []
        this.heldSpeed2 = null
        this.active = true
        this.setTakeover(true)
        this.say(`Agent taking over: "${goal}". Say \`/agent stop\` to reclaim control.`)
        this.scheduleNext(0)
    }

    stop(message = "Agent stopped.") {
        this.active = false
        if (this.loopTimer) {
            clearTimeout(this.loopTimer)
            this.loopTimer = null
        }
        this.heldSpeed2 = null
        const p = this.client.clientGameLogic.playerEntity
        if (p) {
            p.speed2 = { x: 0, y: 0 }
            if (p.movement) p.movement.moving = false
        }
        this.setTakeover(false)
        this.say(message)
    }

    /** Record chat the player heard, so the agent has recent context. */
    noteChat(from: string, message: string) {
        if (!message) return
        this.recentChat.push({ from, message })
        if (this.recentChat.length > CHAT_LIMIT) this.recentChat.shift()
    }

    /** Called every frame from Client.update(), between reconciliation and processInputs. */
    onFrame() {
        if (!this.active || !this.heldSpeed2) return
        const p = this.client.clientGameLogic.playerEntity
        if (!p) return
        p.speed2 = { x: this.heldSpeed2.x, y: this.heldSpeed2.y }
        p.speed = p.maxSpeed
        p.movement = {
            direction: p.movement?.direction,
            ...(p.movement || {}),
            moving: true,
            angle: Math.atan2(this.heldSpeed2.y, this.heldSpeed2.x),
        } as any
    }

    // --- MovementSink for the action dispatcher ---
    setHeldMovement(v: { x: number; y: number } | null) {
        this.heldSpeed2 = v
        if (!v) {
            const p = this.client.clientGameLogic.playerEntity
            if (p) {
                p.speed2 = { x: 0, y: 0 }
                if (p.movement) p.movement.moving = false
            }
        }
    }

    private setTakeover(on: boolean) {
        const pc = this.client.clientGameLogic.playerController
        if (!pc) return
        pc.enabledKeyboardAction(!on)
        pc.enabledMouseAction(!on)
    }

    private scheduleNext(delayMs: number) {
        if (!this.active) return
        this.loopTimer = setTimeout(() => this.runTick(), delayMs)
    }

    private async runTick() {
        if (!this.active) return
        const started = Date.now()
        try {
            const obs = buildObservation(this.client.clientGameLogic, this.goal, this.tick, this.recentChat)
            if (obs && !this.inFlight) {
                this.inFlight = true
                const action = await this.decide(obs)
                this.inFlight = false
                if (this.active && action) {
                    const desc = applyAction(this.client, this, action)
                    // eslint-disable-next-line no-console
                    console.log(`[agent tick ${this.tick}] ${desc}${action.reasoning ? " — " + action.reasoning : ""}`)
                    this.history.push({ tick: this.tick, action })
                    if (this.history.length > HISTORY_LIMIT) this.history.shift()
                }
            }
        } catch (e: any) {
            this.inFlight = false
            // eslint-disable-next-line no-console
            console.warn("[agent] decide failed:", e?.message || e)
        }
        this.tick++
        const elapsed = Date.now() - started
        this.scheduleNext(Math.max(0, DECISION_INTERVAL_MS - elapsed))
    }

    private async decide(observation: DecideRequest["observation"]): Promise<AgentAction | null> {
        // Strip `reasoning` from history before sending — the model doesn't need its own past
        // rationales replayed each tick, and they add up over the rolling window.
        const history = this.history.map(h => {
            const { reasoning, ...action } = h.action as any
            return { tick: h.tick, action }
        })
        const body: DecideRequest = { observation, history }
        const operatorToken = window.sessionStorage.getItem("samsara.admin.token")
        const resp = await fetch(decideEndpoint(), {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(operatorToken ? { Authorization: `Bearer ${operatorToken}` } : {}) },
            body: JSON.stringify(body),
        })
        if (resp.status === 401 || resp.status === 429) {
            this.stop("Agent requires an operator session and an available request budget.")
            return null
        }
        if (!resp.ok) throw new Error(`decide HTTP ${resp.status}`)
        const data = (await resp.json()) as DecideResponse
        return data?.action ?? null
    }

    private say(message: string) {
        // eslint-disable-next-line no-console
        console.log(`[agent] ${message}`)
        const gui: any = this.client.clientGameLogic.clientRenderer?.gui?.()
        if (gui?.showScreenMessage) {
            try {
                gui.showScreenMessage(message)
            } catch {
                /* best-effort UI feedback */
            }
        }
    }
}
